import * as React from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from "@/components/ui/chart";
import {
    CartesianGrid,
    Line,
    LineChart,
    XAxis,
    YAxis,
    ErrorBar,
    Area,
    AreaChart,
    ReferenceLine,
} from "recharts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const chartConfig = {
    value: {
        label: "Value",
        color: "hsl(var(--chart-2))",
    },
} satisfies ChartConfig;

export default function ViewOptimizationDialog({
    product,
    isOpen,
    onClose,
}: {
    product: any;
    isOpen: boolean;
    onClose: () => void;
}) {
    const { shop } = useAuth();

    const markets = shop?.markets;
    const [selectedMarket, setSelectedMarket] = React.useState<string>(
        markets?.[0]?.handle ?? ""
    );

    if (!product?.optimization_progress) return null;

    // Find the marketset index that contains the selected market's handle
    const marketsetIndex = product.optimization_progress.marketsets.findIndex(
        (marketset: any) => marketset.markets.includes(selectedMarket)
    );

    const data =
        product.optimization_progress.marketsets[marketsetIndex]?.data ?? null;

    if (data === null) return null;

    // Downsample function - takes every nth point
    const downsample = (data: any[], n: number) => {
        return data.filter((_, index) => index % n === 0);
    };

    // Transform y-axis values to profit per 1000 visitors
    const transformToProfitPer1kVisitors = (value: number) => {
        return value * 1000; // Multiply by 1000 to get profit per 1000 visitors
    };

    const y_axis_label = "Daily Gross Profit (£)";

    const samplingRate = 1; // Show all data points
    const chartData = data?.plot_data?.x_values?.map(
        (x: number, index: number) => ({
            x: x,
            y: transformToProfitPer1kVisitors(data?.plot_data?.y_values[index]),
            y_lower: transformToProfitPer1kVisitors(
                data?.plot_data?.y_lower[index]
            ),
            y_upper: transformToProfitPer1kVisitors(
                data?.plot_data?.y_upper[index]
            ),
            error: transformToProfitPer1kVisitors(
                data?.plot_data?.y_upper[index] -
                    data?.plot_data?.y_lower[index]
            ),
        })
    );

    const downsampledChartData = downsample(chartData || [], samplingRate);

    // const conversionRate =
    //     data.timestamps?.map((_: any, i: number) =>
    //         data.visitors?.[i]
    //             ? ((data.sales?.[i] / data.visitors[i]) * 100).toFixed(1)
    //             : "0.0"
    //     ) ?? [];

    // Get optimal price (last entry in best_prices)
    // const optimalPrice =
    //     Array.isArray(data.best_prices) && data.best_prices.length > 0
    //         ? data.best_prices[data.best_prices.length - 1]
    //         : null;
    const originalPrice = data?.original_price ?? null;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[1000px]">
                <DialogHeader>
                    <DialogTitle>Optimization Data</DialogTitle>
                    <DialogDescription>
                        View optimization data for this product.
                    </DialogDescription>
                </DialogHeader>
                <div className="mb-4">
                    <Select
                        value={selectedMarket}
                        onValueChange={setSelectedMarket}
                    >
                        <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder="Select market" />
                        </SelectTrigger>
                        <SelectContent>
                            {markets?.map(
                                (market: {
                                    id: string;
                                    name: string;
                                    handle: string;
                                }) => (
                                    <SelectItem
                                        key={market.id}
                                        value={market.handle}
                                    >
                                        {market.name}
                                    </SelectItem>
                                )
                            )}
                        </SelectContent>
                    </Select>
                </div>
                {/* {JSON.stringify(data.plot_data)} */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg mb-4">
                    <div>
                        <p className="text-sm text-muted-foreground">
                            Last Updated
                        </p>
                        <p className="font-medium">
                            {data.last_updated && data.last_updated !== 0
                                ? new Date(data.last_updated).toLocaleString()
                                : "N/A"}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">
                            Product Cost
                        </p>
                        <p className="font-medium">
                            £{data.product_cost?.toFixed(2) ?? "0.00"}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">
                            Original Price
                        </p>
                        <p className="font-medium">
                            £{data.original_price?.toFixed(2)}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">
                            Current Test Price
                        </p>
                        <p className="font-medium">
                            £{data.current_test_price?.toFixed(2)}
                        </p>
                    </div>
                </div>

                <Tabs defaultValue="chart" className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="chart">Chart</TabsTrigger>
                        <TabsTrigger value="table">Table</TabsTrigger>
                    </TabsList>
                    <TabsContent value="chart">
                        {downsampledChartData?.length > 0 ? (
                            <ChartContainer
                                config={chartConfig}
                                className="h-[400px] w-full mx-auto"
                            >
                                <AreaChart
                                    accessibilityLayer
                                    data={downsampledChartData}
                                    margin={{
                                        top: 20,
                                        right: 30,
                                        left: 20,
                                        bottom: 20,
                                    }}
                                    animationDuration={200}
                                    animationBegin={0}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="x"
                                        tickLine={false}
                                        axisLine={true}
                                        tickMargin={1}
                                        tickFormatter={(value) =>
                                            `£${value.toFixed(0)}`
                                        }
                                        label={{
                                            value: "Price (£)",
                                            position: "bottom",
                                            offset: 0,
                                        }}
                                        type="number"
                                        domain={["dataMin", "dataMax"]}
                                    />
                                    <YAxis
                                        tickLine={false}
                                        axisLine={true}
                                        tickMargin={1}
                                        type="number"
                                        domain={["dataMin", "dataMax"]}
                                        tickFormatter={(value) => {
                                            if (Math.abs(value) >= 1000) {
                                                return `£${(
                                                    value / 1000
                                                ).toFixed(2)}k`;
                                            }
                                            return `£${value.toFixed(2)}`;
                                        }}
                                        label={{
                                            value: y_axis_label,
                                            angle: -90,
                                            dx: -25,
                                        }}
                                    />
                                    <ChartTooltip
                                        content={<ChartTooltipContent />}
                                        formatter={(
                                            value: any,
                                            name: string,
                                            props: any
                                        ) => {
                                            const dataPoint = props.payload;
                                            if (name === "y") {
                                                return [
                                                    `£${Number(value).toFixed(
                                                        2
                                                    )} (${Number(
                                                        dataPoint.y_lower
                                                    ).toFixed(2)} - ${Number(
                                                        dataPoint.y_upper
                                                    ).toFixed(2)})`,
                                                ];
                                            }
                                            return [value, name];
                                        }}
                                    />
                                    {/* <defs>
                                        <linearGradient
                                            id="colorValue"
                                            x1="0"
                                            y1="0"
                                            x2="0"
                                            y2="1"
                                        >
                                            <stop
                                                offset="5%"
                                                stopColor="var(--color-value)"
                                                stopOpacity={0.8}
                                            />
                                            <stop
                                                offset="95%"
                                                stopColor="var(--color-value)"
                                                stopOpacity={0.1}
                                            />
                                        </linearGradient>
                                    </defs> */}
                                    <Area
                                        type="monotone"
                                        dataKey="y"
                                        stroke="var(--color-value)"
                                        strokeWidth={2}
                                        fill="url(#colorValue)"
                                        dot={false}
                                        animationDuration={500}
                                        animationBegin={0}
                                    />

                                    {/* Reference line for original price */}
                                    {originalPrice && (
                                        <ReferenceLine
                                            x={originalPrice}
                                            stroke="#ff6b6b"
                                            strokeDasharray="6 6"
                                            strokeWidth={2}
                                            label={{
                                                value: "Original",
                                                position: "right",
                                                fill: "#ff6b6b",
                                                fontSize: 12,
                                            }}
                                        />
                                    )}

                                    {/* Reference line for optimal price */}
                                    {/* {optimalPrice && (
                                        <ReferenceLine
                                            x={optimalPrice}
                                            stroke="#000000"
                                            strokeDasharray="6 6"
                                            strokeWidth={2}
                                            label={{
                                                value: "Optimal",
                                                position: "right",
                                                fill: "#000000",
                                                fontSize: 12,
                                            }}
                                        />
                                    )} */}
                                </AreaChart>
                            </ChartContainer>
                        ) : (
                            <div className="h-[400px] w-full flex items-center justify-center bg-slate-50 rounded-lg">
                                <div className="text-center">
                                    <p className="text-lg font-medium text-muted-foreground">
                                        No data yet
                                    </p>
                                    <p className="text-sm text-muted-foreground mt-2">
                                        Please wait a little longer until the
                                        optimization returns some results.
                                    </p>
                                </div>
                            </div>
                        )}
                    </TabsContent>
                    <TabsContent
                        value="table"
                        className="h-[400px] overflow-auto"
                    >
                        <div className="rounded-md border">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Timestamp</TableHead>
                                        <TableHead>Test Price</TableHead>
                                        <TableHead>Best Price</TableHead>
                                        <TableHead>Conversion Rate</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data.timestamps?.map(
                                        (timestamp: string, index: number) => (
                                            <TableRow key={timestamp}>
                                                <TableCell>
                                                    {new Date(
                                                        timestamp
                                                    ).toLocaleString()}
                                                </TableCell>
                                                <TableCell>
                                                    £
                                                    {data.prices[index].toFixed(
                                                        2
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    £
                                                    {data.best_prices?.[
                                                        index
                                                    ]?.toFixed(2) ?? "N/A"}
                                                </TableCell>
                                                <TableCell>
                                                    {/* {conversionRate[index]}% */}
                                                    -
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
}
